import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Auth } from '@aws-amplify/auth'
import { Hub } from '@aws-amplify/core'
import { useNavigate, useLocation } from 'react-router-dom'
import { environment } from '@/config/environment'

const INACTIVITY_TIMEOUT_MS = 2_700_000 // 45 minutes

let amplifyConfigured = false
function ensureAmplifyConfigured() {
  if (amplifyConfigured) return
  Auth.configure({
    region: environment.AWS_REGION,
    identityPoolId: environment.COGNITO_IDENTITY_POOL_ID,
    userPoolId: environment.COGNITO_USER_POOL_ID,
    userPoolWebClientId: environment.COGNITO_USER_POOL_WEB_CLIENT_ID,
  })
  amplifyConfigured = true
}

const AuthContext = createContext()

export function AuthProvider({ children }) {
  ensureAmplifyConfigured()

  const navigate = useNavigate()
  const location = useLocation()

  const [cognitoUser, setCognitoUser] = useState(null)
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [groups, setGroups] = useState([])
  const [authState, setAuthState] = useState({
    challenge: undefined,
    isAuthed: false,
    emailVerified: false,
  })

  const inactivityTimerRef = useRef(null)
  const tokenRefreshListenersRef = useRef([])

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current)
      inactivityTimerRef.current = null
    }
  }, [])

  const signOutInternal = useCallback(async () => {
    try { await Auth.signOut() } catch { /* ignore */ }
    setCognitoUser(null)
    setUser(null)
    setGroups([])
    setAuthState({ challenge: undefined, isAuthed: false, emailVerified: false })
    clearInactivityTimer()
  }, [clearInactivityTimer])

  const resetInactivityTimer = useCallback(() => {
    clearInactivityTimer()
    inactivityTimerRef.current = window.setTimeout(() => {
      signOutInternal().then(() => navigate('/login'))
    }, INACTIVITY_TIMEOUT_MS)
  }, [clearInactivityTimer, signOutInternal, navigate])

  const startInactivityTimer = useCallback(() => {
    const handler = () => resetInactivityTimer()
    window.addEventListener('mousemove', handler, true)
    window.addEventListener('keydown', handler, true)
    resetInactivityTimer()
    return () => {
      window.removeEventListener('mousemove', handler, true)
      window.removeEventListener('keydown', handler, true)
      clearInactivityTimer()
    }
  }, [resetInactivityTimer, clearInactivityTimer])

  const extractUserDetails = useCallback(async (cogUser) => {
    try {
      const currentUser = await Auth.currentAuthenticatedUser()
      const attrs = currentUser.attributes || {}
      const session = await Auth.currentSession()
      const userGroups = session.getAccessToken().payload['cognito:groups'] || []

      const details = {
        id: attrs.sub,
        sub: attrs.sub,
        name: `${attrs.given_name || ''} ${attrs.family_name || ''}`.trim(),
        email: attrs.email,
        firstName: attrs.given_name || '',
        lastName: attrs.family_name || '',
      }

      setUser(details)
      setGroups(userGroups)
      return { details, groups: userGroups }
    } catch {
      return null
    }
  }, [])

  const checkEmailVerified = useCallback(async (cogUser) => {
    try {
      const verifiedContacts = await Auth.verifiedContact(cogUser)
      return !!verifiedContacts?.verified?.email
    } catch {
      return false
    }
  }, [])

  // Check for existing session on mount
  useEffect(() => {
    ;(async () => {
      try {
        const currentUser = await Auth.currentAuthenticatedUser()
        const session = await Auth.currentSession()
        if (session.isValid()) {
          setCognitoUser(currentUser)
          const emailVerified = await checkEmailVerified(currentUser)
          setAuthState({ challenge: undefined, isAuthed: true, emailVerified })
          await extractUserDetails(currentUser)
        }
      } catch {
        setAuthState({ challenge: undefined, isAuthed: false, emailVerified: false })
      } finally {
        setLoading(false)
      }
    })()
  }, [checkEmailVerified, extractUserDetails])

  // Hub listener for token refresh and sign events
  useEffect(() => {
    const listener = Hub.listen('auth', async (data) => {
      switch (data.payload.event) {
        case 'tokenRefresh':
          await extractUserDetails(cognitoUser)
          try {
            const jwt = await getUserJWTInternal()
            tokenRefreshListenersRef.current.forEach(fn => fn(jwt))
          } catch { /* ignore */ }
          break
        case 'tokenRefresh_failure':
          await signOutInternal()
          navigate('/login')
          break
        case 'signOut':
          clearInactivityTimer()
          break
      }
    })
    return () => listener()
  }, [cognitoUser, extractUserDetails, signOutInternal, clearInactivityTimer, navigate])

  // Start inactivity timer when authenticated
  useEffect(() => {
    if (authState.isAuthed && authState.emailVerified && user) {
      return startInactivityTimer()
    }
  }, [authState.isAuthed, authState.emailVerified, user, startInactivityTimer])

  // Navigate to dashboard when fully authenticated and on login page
  useEffect(() => {
    if (authState.isAuthed && authState.emailVerified && user && location.pathname === '/login') {
      navigate('/overview')
    }
  }, [authState.isAuthed, authState.emailVerified, user, location.pathname, navigate])

  async function getUserJWTInternal() {
    const currentUser = await Auth.currentAuthenticatedUser()
    return currentUser.getSignInUserSession().getIdToken().getJwtToken()
  }

  const signIn = useCallback(async (email, password) => {
    const cogUser = await Auth.signIn(email, password)
    setCognitoUser(cogUser)

    const challengeName = cogUser.challengeName
    if (challengeName === 'SOFTWARE_TOKEN_MFA') {
      setAuthState(prev => ({ ...prev, challenge: 'SOFTWARE_TOKEN_MFA' }))
      return { challenge: 'SOFTWARE_TOKEN_MFA' }
    }
    if (challengeName === 'MFA_SETUP') {
      setAuthState(prev => ({ ...prev, challenge: 'MFA_SETUP' }))
      return { challenge: 'MFA_SETUP' }
    }
    if (challengeName === 'NEW_PASSWORD_REQUIRED') {
      setAuthState(prev => ({ ...prev, challenge: 'NEW_PASSWORD_REQUIRED' }))
      return { challenge: 'NEW_PASSWORD_REQUIRED' }
    }

    const emailVerified = await checkEmailVerified(cogUser)
    setAuthState({ challenge: undefined, isAuthed: true, emailVerified })
    await extractUserDetails(cogUser)
    return { challenge: undefined }
  }, [checkEmailVerified, extractUserDetails])

  const confirmSignIn = useCallback(async (totpCode) => {
    if (!cognitoUser) throw new Error('No active sign-in session')
    const confirmedUser = await Auth.confirmSignIn(cognitoUser, totpCode, 'SOFTWARE_TOKEN_MFA')
    setCognitoUser(confirmedUser)
    const emailVerified = await checkEmailVerified(confirmedUser)
    setAuthState({ challenge: undefined, isAuthed: true, emailVerified })
    await extractUserDetails(confirmedUser)
  }, [cognitoUser, checkEmailVerified, extractUserDetails])

  const beginSetupTOTP = useCallback(async () => {
    if (!cognitoUser) throw new Error('No active sign-in session')
    return await Auth.setupTOTP(cognitoUser)
  }, [cognitoUser])

  const confirmSetupTOTP = useCallback(async (totpCode) => {
    if (!cognitoUser) throw new Error('No active sign-in session')
    await Auth.verifyTotpToken(cognitoUser, totpCode)
    const newUser = await Auth.currentAuthenticatedUser()
    setCognitoUser(newUser)
    const emailVerified = await checkEmailVerified(newUser)
    setAuthState({ challenge: undefined, isAuthed: true, emailVerified })
    await extractUserDetails(newUser)
  }, [cognitoUser, checkEmailVerified, extractUserDetails])

  const signOut = useCallback(async () => {
    await signOutInternal()
    navigate('/login')
  }, [signOutInternal, navigate])

  const sendForgotPasswordCode = useCallback(async (email) => {
    await Auth.forgotPassword(email)
  }, [])

  const verifyForgotPasswordCode = useCallback(async (email, code, newPassword) => {
    await Auth.forgotPasswordSubmit(email, code, newPassword)
  }, [])

  const forcePasswordReset = useCallback(async (newPassword) => {
    if (!cognitoUser) throw new Error('No active sign-in session')
    const updatedUser = await Auth.completeNewPassword(cognitoUser, newPassword)
    setCognitoUser(updatedUser)
    setAuthState(prev => ({ ...prev, challenge: undefined }))
  }, [cognitoUser])

  const sendEmailVerification = useCallback(async () => {
    if (!cognitoUser) throw new Error('No active sign-in session')
    await Auth.verifyUserAttribute(cognitoUser, 'email')
  }, [cognitoUser])

  const confirmEmailVerification = useCallback(async (code) => {
    if (!cognitoUser) throw new Error('No active sign-in session')
    await Auth.verifyUserAttributeSubmit(cognitoUser, 'email', code)
    setAuthState(prev => ({ ...prev, emailVerified: true }))
  }, [cognitoUser])

  const getUserJWT = useCallback(async () => {
    return getUserJWTInternal()
  }, [])

  const updateUserNames = useCallback(async (firstName, lastName) => {
    const currentUser = await Auth.currentAuthenticatedUser()
    await Auth.updateUserAttributes(currentUser, {
      given_name: firstName,
      family_name: lastName,
    })
    await extractUserDetails(currentUser)
  }, [extractUserDetails])

  const updateEmail = useCallback(async (newEmail) => {
    const currentUser = await Auth.currentAuthenticatedUser()
    await Auth.updateUserAttributes(currentUser, { email: newEmail })
  }, [])

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    const currentUser = await Auth.currentAuthenticatedUser()
    await Auth.changePassword(currentUser, currentPassword, newPassword)
  }, [])

  const onTokenRefresh = useCallback((fn) => {
    tokenRefreshListenersRef.current.push(fn)
    return () => {
      tokenRefreshListenersRef.current = tokenRefreshListenersRef.current.filter(f => f !== fn)
    }
  }, [])

  const hasAdminPermissions = useMemo(
    () => groups.some(g => g.toLowerCase() === 'admin'),
    [groups]
  )

  const hasWritePermissions = useMemo(
    () => groups.some(g => {
      const lower = g.toLowerCase()
      return lower === 'admin' || lower === 'manager'
    }),
    [groups]
  )

  const isAuthenticated = authState.isAuthed && authState.emailVerified

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      authState,
      groups,
      signIn,
      signOut,
      confirmSignIn,
      beginSetupTOTP,
      confirmSetupTOTP,
      sendForgotPasswordCode,
      verifyForgotPasswordCode,
      forcePasswordReset,
      sendEmailVerification,
      confirmEmailVerification,
      getUserJWT,
      updateUserNames,
      updateEmail,
      changePassword,
      onTokenRefresh,
      hasAdminPermissions,
      hasWritePermissions,
      isAuthenticated,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
